import type { DisplayJob } from '../types';
import { buildFinlandCountryOnlyJobLocationPlace } from './finland-country-job-location-place';
import { BASE_URL } from '../../utils/constants/url-constants';
import { matchesLocation, buildFullCompanyAddress } from '../geocoding/match-job-location-with-company-address';
import { joinDisplayJobTextList } from './join-display-job-text-list';

export interface JobPostingSchemaOptions {
  jobLocationType?: 'ON_SITE' | 'HYBRID' | 'TELECOMMUTE';
  jobLocations?: Array<{
    addressLocality?: string;
    addressRegion?: string;
    addressCountry?: string;
  }>;
  applicantLocationRequirements?: Record<string, unknown> | Record<string, unknown>[];
  disableSalary?: boolean;
}

/**
 * Generates a JobPosting schema.org object from a DisplayJob.
 * Returns { schema, isContentComplete, missingFields }
 * - schema: the JSON-LD object
 * - isContentComplete: true if Google-essential fields are filled
 * - missingFields: array of missing field names
 * Accepts locale, applyUrl (ATS URL for apply), and canonicalUrl (JobCrawls URL for ownership).
 */
export function generateJobPostingSchema(
  job: DisplayJob,
  locale: string = 'fi',
  applyUrl?: string,
  canonicalUrl?: string,
  options: JobPostingSchemaOptions = {}
) {
  // Helper: treat empty string as missing
  const clean = <T>(val: T | undefined | null): T | undefined => {
    if (val === undefined || val === null) return undefined;
    if (typeof val === 'string' && val.trim() === '') return undefined;
    if (Array.isArray(val) && val.length === 0) return undefined;
    return val;
  };

  // Some crawled company URLs are two URLs joined by ';' (e.g. a redirect + the real site).
  // schema.org's `url` expects a single value, so take the first one.
  const firstUrl = (raw: string | undefined): string | undefined => {
    const c = clean(raw);
    if (!c) return undefined;
    const first = c.split(';')[0]?.trim();
    return first || undefined;
  };
  const companyURL = firstUrl(job.companyURL);

  // --- Date Handling ---
  function toISODate(val: unknown, fallback: Date): string {
    if (!val) return fallback.toISOString().split('T')[0];
    if (typeof val === 'number') {
      // Assume unix timestamp (seconds or ms)
      const ts = val > 1e12 ? val : val * 1000;
      return new Date(ts).toISOString().split('T')[0];
    }
    if (typeof val === 'string') {
      const d = new Date(val);
      if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
    }
    return fallback.toISOString().split('T')[0];
  }
  const today = new Date();
  // Anchor the validThrough fallback to lastSeen (last confirmed crawl sighting), not render time.
  // Otherwise validThrough rolls forward by 30 days on every render forever, even for jobs the
  // crawler has stopped seeing but haven't yet crossed the 410 staleness threshold.
  const parsedLastSeen = job.lastSeen ? new Date(job.lastSeen) : undefined;
  const lastSeenDate = parsedLastSeen && !isNaN(parsedLastSeen.getTime()) ? parsedLastSeen : today;
  const in30 = new Date(lastSeenDate.getTime() + 30 * 24 * 60 * 60 * 1000);
  const datePosted = toISODate(job.postedDate, today);
  const validThrough = toISODate(job.applicationDeadline, in30);

  // --- employmentType mapping to Google's expected enum ---
  const employmentTypeMap: Record<string, string> = {
    // English
    'full-time': 'FULL_TIME',
    'full time': 'FULL_TIME',
    'fulltime': 'FULL_TIME',
    'part-time': 'PART_TIME',
    'part time': 'PART_TIME',
    'parttime': 'PART_TIME',
    'contract': 'CONTRACTOR',
    'contractor': 'CONTRACTOR',
    'temporary': 'TEMPORARY',
    'internship': 'INTERN',
    'intern': 'INTERN',
    // Finnish
    'kokoaikainen': 'FULL_TIME',
    'osa-aikainen': 'PART_TIME',
    'sopimuspohjainen työ': 'CONTRACTOR',
    'sopimuspohjainen': 'CONTRACTOR',
    'määräaikainen': 'TEMPORARY',
    'harjoittelupaikka': 'INTERN',
    'harjoittelija': 'INTERN',
    // Swedish
    'heltid': 'FULL_TIME',
    'deltid': 'PART_TIME',
    'kontrakt': 'CONTRACTOR',
    'tidsbegränsad': 'TEMPORARY',
    'praktik': 'INTERN',
    'praktikant': 'INTERN',
  };
  const normalizedJobType = ((job.jobType || '').toLowerCase().trim());
  const employmentType = employmentTypeMap[normalizedJobType] || 'FULL_TIME';

  // --- logoUrl construction (absolute, using WebP 128px) ---
  function getCompanyLogoUrl(companyId: string): string {
    if (!companyId) {
      return `${BASE_URL}/company-logo-default.svg`;
    }
    const kebab = (companyId || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    // Use WebP 128px as default (good balance between quality and size)
    return `${BASE_URL}/logos/${kebab}-128.webp`;
  }
  const logoUrl = getCompanyLogoUrl(job.companyId ?? '');

  // --- jobLocation parsing (array support) ---
  const countryMap: Record<string, string> = {
    'finland': 'FI',
    'suomi': 'FI',
    'sweden': 'SE',
    'sverige': 'SE',
    'norway': 'NO',
    'norge': 'NO',
    'denmark': 'DK',
    'danmark': 'DK',
    'estonia': 'EE',
    'eesti': 'EE',
    'latvia': 'LV',
    'lithuania': 'LT',
    'germany': 'DE',
    'saksa': 'DE',
    'poland': 'PL',
    'france': 'FR',
    'spain': 'ES',
    'italy': 'IT',
    'portugal': 'PT',
    'netherlands': 'NL',
    'belgium': 'BE',
    'austria': 'AT',
    'switzerland': 'CH',
    'swiss': 'CH',
    'ireland': 'IE',
    'united states': 'US',
    'usa': 'US',
    'yhdysvallat': 'US',
    'united kingdom': 'GB',
    'uk': 'GB',
    'england': 'GB',
    'scotland': 'GB',
    'wales': 'GB',
    'northern ireland': 'GB',
    'canada': 'CA',
    'australia': 'AU',
    'new zealand': 'NZ',
    'india': 'IN',
    'singapore': 'SG',
  };

  const isoToCountryName: Record<string, string> = {
    FI: 'Finland',
    SE: 'Sweden',
    NO: 'Norway',
    DK: 'Denmark',
    EE: 'Estonia',
    LV: 'Latvia',
    LT: 'Lithuania',
    DE: 'Germany',
    PL: 'Poland',
    FR: 'France',
    ES: 'Spain',
    IT: 'Italy',
    PT: 'Portugal',
    NL: 'Netherlands',
    BE: 'Belgium',
    AT: 'Austria',
    CH: 'Switzerland',
    IE: 'Ireland',
    US: 'United States',
    GB: 'United Kingdom',
    CA: 'Canada',
    AU: 'Australia',
    NZ: 'New Zealand',
    IN: 'India',
    SG: 'Singapore',
  };

  const cityCountryMap: Record<string, string> = {
    'helsinki': 'FI',
    'helsingfors': 'FI',
    'espoo': 'FI',
    'esbo': 'FI',
    'vantaa': 'FI',
    'vanda': 'FI',
    'tampere': 'FI',
    'tammerfors': 'FI',
    'turku': 'FI',
    'åbo': 'FI',
    'abo': 'FI',
    'oulu': 'FI',
    'uleåborg': 'FI',
    'uleaborg': 'FI',
    'lahti': 'FI',
    'kuopio': 'FI',
    'jyväskylä': 'FI',
    'jyvaskyla': 'FI',
    'pori': 'FI',
    'björneborg': 'FI',
    'bjorneborg': 'FI',
    'lappeenranta': 'FI',
    'villmanstrand': 'FI',
    'kotka': 'FI',
    'joensuu': 'FI',
    'rovaniemi': 'FI',
    'seinäjoki': 'FI',
    'seinajoki': 'FI',
    'mikkeli': 'FI',
    'sankt michel': 'FI',
    'hämeenlinna': 'FI',
    'hameenlinna': 'FI',
    'hyvinkää': 'FI',
    'hyvinkaa': 'FI',
    'salo': 'FI',
    'nurmijärvi': 'FI',
    'nurmijarvi': 'FI',
    'kemi': 'FI',
    'tallinn': 'EE',
    'stockholm': 'SE',
    'gothenburg': 'SE',
    'göteborg': 'SE',
    'malmö': 'SE',
    'malmo': 'SE',
    'oslo': 'NO',
    'copenhagen': 'DK',
    'köbenhavn': 'DK',
    'kobenhavn': 'DK',
    'berlin': 'DE',
    'munich': 'DE',
    'münchen': 'DE',
    'hamburg': 'DE',
    'london': 'GB',
    'dublin': 'IE',
    'paris': 'FR',
    'madrid': 'ES',
    'rome': 'IT',
  };

  // Finnish city -> region (maakunta), used to fill addressRegion when the location
  // string only has city + country (e.g. "Helsinki, Finland") and lacks an explicit region.
  const finlandCityRegionMap: Record<string, string> = {
    'helsinki': 'Uusimaa',
    'helsingfors': 'Uusimaa',
    'espoo': 'Uusimaa',
    'esbo': 'Uusimaa',
    'vantaa': 'Uusimaa',
    'vanda': 'Uusimaa',
    'hyvinkää': 'Uusimaa',
    'hyvinkaa': 'Uusimaa',
    'nurmijärvi': 'Uusimaa',
    'nurmijarvi': 'Uusimaa',
    'tampere': 'Pirkanmaa',
    'tammerfors': 'Pirkanmaa',
    'turku': 'Varsinais-Suomi',
    'åbo': 'Varsinais-Suomi',
    'abo': 'Varsinais-Suomi',
    'salo': 'Varsinais-Suomi',
    'oulu': 'Pohjois-Pohjanmaa',
    'uleåborg': 'Pohjois-Pohjanmaa',
    'uleaborg': 'Pohjois-Pohjanmaa',
    'lahti': 'Päijät-Häme',
    'kuopio': 'Pohjois-Savo',
    'jyväskylä': 'Keski-Suomi',
    'jyvaskyla': 'Keski-Suomi',
    'pori': 'Satakunta',
    'björneborg': 'Satakunta',
    'bjorneborg': 'Satakunta',
    'lappeenranta': 'Etelä-Karjala',
    'villmanstrand': 'Etelä-Karjala',
    'kotka': 'Kymenlaakso',
    'joensuu': 'Pohjois-Karjala',
    'rovaniemi': 'Lappi',
    'kemi': 'Lappi',
    'seinäjoki': 'Etelä-Pohjanmaa',
    'seinajoki': 'Etelä-Pohjanmaa',
    'mikkeli': 'Etelä-Savo',
    'sankt michel': 'Etelä-Savo',
    'hämeenlinna': 'Kanta-Häme',
    'hameenlinna': 'Kanta-Häme',
  };

  const remotePrefixes = ['remote', 'etätyö', 'etatyö', 'etä', 'eta', 'distans', 'distansarbete'];
  const hybridPrefixes = ['hybrid', 'hybridityö', 'hybridarbete'];
  const onsitePrefixes = ['onsite', 'on-site', 'on site', 'paikkasidonnainen', 'på plats'];

  type ParsedLocation = {
    type: 'remote' | 'hybrid' | 'onsite' | 'general';
    cleaned: string;
    original: string;
  };

  const parseLocationEntry = (loc: string): ParsedLocation | null => {
    if (!loc || typeof loc !== 'string') return null;
    let cleaned = loc.trim();
    if (!cleaned) return null;
    const lower = cleaned.toLowerCase();

    const stripPrefix = (prefix: string) => {
      cleaned = cleaned.slice(prefix.length).trim();
      if (cleaned.startsWith('-') || cleaned.startsWith(',') || cleaned.startsWith(':')) {
        cleaned = cleaned.slice(1).trim();
      }
    };

    if (remotePrefixes.some((prefix) => lower.startsWith(prefix))) {
      const matched = remotePrefixes.find((prefix) => lower.startsWith(prefix))!;
      stripPrefix(matched);
      return { type: 'remote', cleaned, original: loc };
    }

    if (hybridPrefixes.some((prefix) => lower.startsWith(prefix))) {
      const matched = hybridPrefixes.find((prefix) => lower.startsWith(prefix))!;
      stripPrefix(matched);
      return { type: 'hybrid', cleaned, original: loc };
    }

    if (onsitePrefixes.some((prefix) => lower.startsWith(prefix))) {
      const matched = onsitePrefixes.find((prefix) => lower.startsWith(prefix))!;
      stripPrefix(matched);
      return { type: 'onsite', cleaned, original: loc };
    }

    return { type: 'general', cleaned, original: loc };
  };

  const parsedLocations = Array.isArray(job.location)
    ? (job.location as Array<string | undefined | null>)
        .filter((loc): loc is string => typeof loc === 'string' && loc.trim().length > 0)
        .map((loc) => parseLocationEntry(loc))
        .filter((entry): entry is ParsedLocation => entry !== null)
    : [];

  const remoteLocationEntries = parsedLocations.filter((entry) => entry.type === 'remote');
  const hybridLocationEntries = parsedLocations.filter((entry) => entry.type === 'hybrid');
  const physicalLocationEntries = parsedLocations.filter(
    (entry) => entry.type === 'onsite' || entry.type === 'general' || entry.type === 'hybrid'
  );

  const remotenessValue = (job.remoteness || '').toLowerCase();
  const hasRemoteFlag =
    remoteLocationEntries.length > 0 ||
    ['remote', 'etä', 'etätyö', 'distans', 'anywhere', 'global'].some((token) => remotenessValue.includes(token));
  const hasHybridFlag =
    hybridLocationEntries.length > 0 ||
    ['hybrid', 'hybridityö', 'hybridarbete'].some((token) => remotenessValue.includes(token));

  const toCountryCode = (value?: string): string | undefined => {
    if (!value) return undefined;
    const sanitized = value.replace(/\(.*?\)/g, '').trim();
    if (!sanitized) return undefined;
    const lower = sanitized.toLowerCase();
    if (countryMap[lower]) return countryMap[lower];
    if (sanitized.length === 2) return sanitized.toUpperCase();
    return undefined;
  };

  const toTitleCase = (value: string): string =>
    value
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

  const buildPlace = (raw: string) => {
    const segments = raw
      .split(',')
      .map((segment) => segment.trim())
      .filter(Boolean);
    if (segments.length === 0) return null;

    let addressLocality: string | undefined;
    let addressRegion: string | undefined;
    let addressCountry: string | undefined;

    if (segments.length === 1) {
      const segment = segments[0];
      const directCountry = toCountryCode(segment);
      if (directCountry) {
        addressCountry = directCountry;
      } else {
        const cityIso = cityCountryMap[segment.toLowerCase()];
        if (cityIso) {
          addressLocality = toTitleCase(segment);
          addressCountry = cityIso;
        } else {
          addressLocality = toTitleCase(segment);
        }
      }
    } else if (segments.length === 2) {
      addressLocality = segments[0];
      const countryCandidate = toCountryCode(segments[1]) || cityCountryMap[segments[1].toLowerCase()];
      if (countryCandidate) {
        addressCountry = countryCandidate;
      }
    } else if (segments.length >= 3) {
      addressLocality = segments[0];
      addressRegion = segments.slice(1, -1).join(', ');
      const countryCandidate =
        toCountryCode(segments[segments.length - 1]) ||
        cityCountryMap[segments[segments.length - 1].toLowerCase()];
      if (countryCandidate) {
        addressCountry = countryCandidate;
      }
    }

    if (!addressRegion && addressLocality && addressCountry === 'FI') {
      addressRegion = finlandCityRegionMap[addressLocality.toLowerCase()];
    }

    if (!addressLocality && !addressCountry) return null;

    return {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        ...(addressLocality ? { addressLocality } : {}),
        ...(addressRegion ? { addressRegion } : {}),
        ...(addressCountry ? { addressCountry } : {}),
      },
    };
  };

  const placeEntries = physicalLocationEntries
    .map((entry) => buildPlace(entry.cleaned))
    .filter((place): place is Record<string, unknown> => place !== null);
  let jobLocation: Record<string, unknown>[] | undefined = undefined;

  // Check if any job location matches company address (for Finland locations)
  // If so, use the full company address in the schema
  const companyData = job as DisplayJob & {
    address_fi?: string;
    address_sv?: string;
    registryStreet?: string;
    registryBuildingNumber?: string;
    registryPostCode?: string;
    registryCity?: string;
  };
  
  let useFullCompanyAddress = false;
  let matchingLocation: string | undefined;
  
  // Check all physical location entries to see if any match company address
  for (const entry of physicalLocationEntries) {
    const locationString = entry.cleaned || entry.original;
    // Only check Finland locations
    const isFinlandLocation = locationString.toLowerCase().includes('finland') || 
                              locationString.toLowerCase().includes('suomi') ||
                              cityCountryMap[locationString.toLowerCase().split(',')[0]?.trim().toLowerCase()] === 'FI';
    
    if (isFinlandLocation && matchesLocation(locationString, companyData)) {
      useFullCompanyAddress = true;
      matchingLocation = locationString;
      break; // Use first matching location
    }
  }

  const extractRemoteRegions = (entry: ParsedLocation): string[] => {
    const raw = entry.cleaned.trim();
    if (!raw) return [];
    const separators = /(?:,|\/|&|\band\b|\bja\b|\boch\b|\bella\b)/i;
    return raw
      .split(separators)
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
  };

  const mapRegionToRequirement = (region: string) => {
    const lower = region.toLowerCase();
    // Empty region or explicit worldwide scope → no specific country required; caller omits the field
    if (lower === '' || ['global', 'worldwide', 'anywhere', 'globaali', 'globalt'].includes(lower)) {
      return null;
    }
    if (countryMap[lower]) {
      const iso = countryMap[lower];
      return { '@type': 'Country', name: isoToCountryName[iso] ?? toTitleCase(region) };
    }

    const normalized = lower.replace(/\s+/g, '');
    const continentMap: Record<string, string> = {
      europe: 'Europe',
      europa: 'Europe',
      eurooppa: 'Europe',
      eu: 'Europe',
      asia: 'Asia',
      northamerica: 'North America',
      southamerica: 'South America',
      africa: 'Africa',
      oceania: 'Oceania',
    };
    if (continentMap[normalized]) {
      if (['europe', 'eu', 'europa', 'eurooppa'].includes(normalized)) {
        return { '@type': 'Country', name: 'Finland' };
      }
      return { '@type': 'Continent', name: continentMap[normalized] };
    }

    const administrativeAreaFinland: Set<string> = new Set([
      'emea',
      'nordics',
      'nordic',
      'pohjoismaat',
      'scandinavia',
      'skandinavien',
      'baltics',
      'benelux',
    ]);
    if (administrativeAreaFinland.has(normalized)) {
      return { '@type': 'Country', name: 'Finland' };
    }

    if (region.length === 2) {
      const iso = region.toUpperCase();
      return { '@type': 'Country', name: isoToCountryName[iso] ?? iso };
    }

    const fallbackIso = cityCountryMap[lower];
    if (fallbackIso) {
      return { '@type': 'Country', name: isoToCountryName[fallbackIso] ?? fallbackIso };
    }

    return null;
  };

  const remoteRequirements = remoteLocationEntries
    .flatMap((entry) => extractRemoteRegions(entry).map(mapRegionToRequirement))
    .filter((req): req is Record<string, unknown> => req !== null);

  const remoteOverride = options.jobLocationType === 'TELECOMMUTE';
  const remoteIndicated = hasRemoteFlag || remoteOverride;

  let jobLocationType: 'TELECOMMUTE' | undefined = remoteIndicated ? 'TELECOMMUTE' : undefined;

  const overrideJobLocations =
    options.jobLocations && options.jobLocations.length > 0
      ? options.jobLocations.map((loc) => ({
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            ...(loc.addressLocality ? { addressLocality: loc.addressLocality } : {}),
            ...(loc.addressRegion ? { addressRegion: loc.addressRegion } : {}),
            ...(loc.addressCountry ? { addressCountry: loc.addressCountry } : {}),
          },
        }))
      : undefined;

  if (jobLocationType === 'TELECOMMUTE') {
    jobLocation = overrideJobLocations;
  } else {
    if (overrideJobLocations && overrideJobLocations.length > 0) {
      jobLocation = overrideJobLocations;
    } else if (useFullCompanyAddress && matchingLocation) {
      // Use full company address when location matches
      const fullAddress = buildFullCompanyAddress(companyData);
      if (fullAddress) {
        jobLocation = [{
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            ...(fullAddress.streetAddress ? { streetAddress: fullAddress.streetAddress } : {}),
            ...(fullAddress.addressLocality ? { addressLocality: fullAddress.addressLocality } : {}),
            ...(fullAddress.postalCode ? { postalCode: fullAddress.postalCode } : {}),
            addressCountry: fullAddress.addressCountry,
          },
        }];
      } else {
        // Fallback to place entries if full address couldn't be built
        jobLocation = placeEntries.length > 0 ? placeEntries : undefined;
      }
    } else if (placeEntries.length > 0) {
      jobLocation = placeEntries;
    }
  }

  let applicantLocationRequirements: Record<string, unknown> | Record<string, unknown>[] | undefined =
    undefined;
  if (options.applicantLocationRequirements !== undefined) {
    applicantLocationRequirements = options.applicantLocationRequirements;
  } else if (jobLocationType === 'TELECOMMUTE') {
    const dedupedRequirements = remoteRequirements.filter(
      (req, index, arr) =>
        index === arr.findIndex((candidate) => JSON.stringify(candidate) === JSON.stringify(req))
    );

    if (dedupedRequirements.length > 0) {
      applicantLocationRequirements =
        dedupedRequirements.length === 1 ? dedupedRequirements[0] : dedupedRequirements;
    } else {
      // Remote job with no mappable region (including explicit worldwide) — Google requires
      // applicantLocationRequirements on TELECOMMUTE jobs, so default to Finland (our audience).
      applicantLocationRequirements = { '@type': 'Country', name: 'Finland' };
    }
  }

  // --- educationRequirements mapping to Google's expected values ---
  const educationRequirementsMap: Record<string, string> = {
    // English values
    'high school': 'high school',
    'vocational school': 'associate degree',
    'apprenticeship': 'associate degree',
    'bachelor': 'bachelor degree',
    'masters': 'postgraduate degree',
    'doctorate': 'postgraduate degree',
    // Finnish values
    'toisen asteen koulutus': 'high school',
    'oppisopimuskoulutus': 'associate degree',
    'alempi korkeakoulututkinto': 'bachelor degree',
    'ylempi korkeakoulututkinto': 'postgraduate degree',
    'tieteellinen jatkotutkinto': 'postgraduate degree',
    // Swedish values
    'gymnasieutbildning': 'high school',
    'yrkesutbildning': 'associate degree',
    'lärlingsutbildning': 'associate degree',
    'kandidat': 'bachelor degree',
    'doktorand': 'postgraduate degree',
  };
  
  // Convert educationLevel to proper schema format
  let educationRequirements: Record<string, unknown> | undefined = undefined;
  if (clean(job.educationLevel)) {
    const normalizedEducation = (job.educationLevel || '').toLowerCase().trim();
    const mappedValue = educationRequirementsMap[normalizedEducation];
    
    if (mappedValue) {
      educationRequirements = {
        '@type': 'EducationalOccupationalCredential',
        credentialCategory: mappedValue,
      };
    }
  }

  // --- specialCommitments for Finland/Europe ---
  const specialCommitments: string | undefined = undefined; // TODO: Add logic for VeteranCommit etc if applicable

  // --- incentiveCompensation ---
  const incentiveCompensation: string | undefined = job.salaryDetails ? job.salaryDetails : undefined; // TODO: Add more logic if needed

  // --- workHours ---
  const workHours: string | undefined = undefined; // TODO: Add more specific work hours if available

  // --- occupationalCategory ---
  const occupationalCategory: string | undefined = undefined; // TODO: Add occupational category if available

  // --- salary validation helpers ---
  const normalizeCurrency = (currency?: string): string | undefined => {
    if (!currency) return undefined;
    const trimmed = currency.trim().toUpperCase();
    return /^[A-Z]{3}$/.test(trimmed) ? trimmed : undefined;
  };

  const salaryPeriodMap: Record<string, 'YEAR' | 'MONTH' | 'WEEK' | 'DAY' | 'HOUR'> = {
    yearly: 'YEAR',
    annual: 'YEAR',
    year: 'YEAR',
    monthly: 'MONTH',
    month: 'MONTH',
    weekly: 'WEEK',
    week: 'WEEK',
    daily: 'DAY',
    day: 'DAY',
    hourly: 'HOUR',
    hour: 'HOUR',
  };

  const normalizeSalaryPeriod = (period?: string): 'YEAR' | 'MONTH' | 'WEEK' | 'DAY' | 'HOUR' | undefined => {
    if (!period) return undefined;
    return salaryPeriodMap[period.trim().toLowerCase()];
  };

  const SALARY_MAX_VALUE = 1_000_000_000; // Guardrail to avoid absurd placeholders
  const isValidSalaryNumber = (value?: number): value is number =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= SALARY_MAX_VALUE;

  // Default currency to EUR when absent (common for Finnish jobs with no currency set)
  const normalizedCurrency = normalizeCurrency(job.currency) ?? 'EUR';
  // Default period to MONTH only when the field is absent — if an unrecognised value was
  // explicitly set (e.g. "per-project"), leave it undefined so baseSalary is omitted.
  const normalizedUnit = normalizeSalaryPeriod(job.salaryPeriod) ?? (!job.salaryPeriod ? 'MONTH' : undefined);
  const hasMin = job.salaryRangeMin !== undefined && job.salaryRangeMin !== null;
  const hasMax = job.salaryRangeMax !== undefined && job.salaryRangeMax !== null;
  const minSalary = isValidSalaryNumber(job.salaryRangeMin) ? job.salaryRangeMin : undefined;
  const maxSalary = isValidSalaryNumber(job.salaryRangeMax) ? job.salaryRangeMax : undefined;
  const hasInvalidProvided =
    (hasMin && minSalary === undefined) || (hasMax && maxSalary === undefined);

  let baseSalary: Record<string, unknown> | undefined = undefined;

  if (
    normalizedCurrency &&
    normalizedUnit &&
    !hasInvalidProvided &&
    (minSalary !== undefined || maxSalary !== undefined)
  ) {
    // Reject inverted ranges
    if (minSalary !== undefined && maxSalary !== undefined && minSalary > maxSalary) {
      // Skip salary altogether rather than swapping implicitly
    } else {
      const salaryValue: Record<string, unknown> = {
        '@type': 'QuantitativeValue',
        unitText: normalizedUnit,
      };

      if (minSalary !== undefined && maxSalary !== undefined) {
        salaryValue.minValue = minSalary;
        salaryValue.maxValue = maxSalary;
        salaryValue.value = (minSalary + maxSalary) / 2;
      } else if (minSalary !== undefined) {
        salaryValue.value = minSalary;
      } else if (maxSalary !== undefined) {
        salaryValue.value = maxSalary;
      }

      baseSalary = {
        '@type': 'MonetaryAmount',
        currency: normalizedCurrency,
        value: salaryValue,
      };
    }
  }

  if (options.disableSalary) {
    baseSalary = undefined;
  }

  // --- hiringOrganization.sameAs ---
  let sameAs: string | undefined = undefined;
  if (applyUrl) {
    try {
      sameAs = new URL(applyUrl).origin;
    } catch {
      try {
        sameAs = companyURL ? new URL(companyURL).origin : undefined;
      } catch {
        sameAs = undefined;
      }
    }
  } else if (companyURL) {
    try {
      sameAs = new URL(companyURL).origin;
    } catch {
      sameAs = undefined;
    }
  }

  const canonicalClean = clean(canonicalUrl);
  const jobUrlClean = clean(job.url);
  const finalPageUrl = canonicalClean || jobUrlClean;

  // Map DisplayJob fields to schema.org/JobPosting
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title: clean(job.title || job.normalisedTitle),
    // Prefer original job text for schema; Google recommends the full original description
    description: clean(job.originalDescription || job.description),
    datePosted,
    validThrough,
    employmentType,
    hiringOrganization: {
      '@type': 'Organization',
      name: clean(job.companyId),
      sameAs: sameAs,
      logo: logoUrl,
      description: clean(job.employerOverview),
      url: companyURL,
    },
    jobLocation,
    applicantLocationRequirements,
    // Only include jobLocationType for remote jobs (TELECOMMUTE)
    ...(jobLocationType ? { jobLocationType } : {}),
    ...(baseSalary ? { baseSalary } : {}),
    directApply: false, // TODO: Set to true if direct apply is supported
    identifier: {
      '@type': 'PropertyValue',
      name: clean(job.companyId),
      value: clean(job.objectID),
    },
    educationRequirements,
    experienceRequirements: clean(job.minimumYearsOfExperience)
      ? {
          '@type': 'OccupationalExperienceRequirements',
          monthsOfExperience: Number(job.minimumYearsOfExperience) * 12,
        }
      : undefined,
    experienceInPlaceOfEducation: clean(job.minimumYearsOfExperience) && !clean(job.educationLevel) ? true : undefined,
    incentiveCompensation,
    industry: clean(job.companyIndustry),
    jobBenefits: joinDisplayJobTextList(job.benefits),
    jobImmediateStart: undefined, // TODO: Add logic if available
    occupationalCategory: clean(job.field),
    physicalRequirement: job.isDriversLicenseRequired ? 'Driver\u2019s licence required' : undefined,
    qualifications: joinDisplayJobTextList(job.requirements),
    responsibilities: joinDisplayJobTextList(job.responsibilities),
    skills: joinDisplayJobTextList(job.skillsAndTechnologies),
    specialCommitments,
    totalJobOpenings: 1,
    workHours,
    // Use the company logo we control; never use heroImage which may be an unrelated crawled asset
    image: logoUrl,
    // Use JobCrawls URL for ownership, not ATS URL
    ...(finalPageUrl ? { mainEntityOfPage: finalPageUrl } : {}),
    ...(finalPageUrl ? { url: finalPageUrl } : {}),
    inLanguage: locale,
  };

  const ensureIsoCountry = (place: any): boolean => {
    if (!place || typeof place !== 'object') return false;
    const address = place.address;
    if (!address || typeof address !== 'object') return false;
    const country = address.addressCountry;
    if (typeof country !== 'string') return false;
    return /^[A-Z]{2}$/.test(country.trim());
  };

  if (schema.jobLocationType && schema.jobLocationType !== 'TELECOMMUTE') {
    delete schema.jobLocationType;
  }

  if (schema.jobLocationType === 'TELECOMMUTE') {
    if (!remoteIndicated) {
      delete schema.jobLocationType;
      delete schema.applicantLocationRequirements;
    } else {
      // Google requires applicantLocationRequirements on TELECOMMUTE jobs; if the caller
      // explicitly cleared it (schemaOverrides), drop jobLocationType rather than emit an
      // invalid remote posting.
      if (!schema.applicantLocationRequirements) {
        delete schema.jobLocationType;
      }
      if (schema.jobLocation) {
        delete schema.jobLocation;
      }
    }
  } else {
    const locationsArray = Array.isArray(schema.jobLocation) ? schema.jobLocation : [];
    let hasIso = locationsArray.length > 0 && locationsArray.every(ensureIsoCountry);
    if (!hasIso) {
      const fallbackLocations =
        overrideJobLocations && overrideJobLocations.length > 0
          ? overrideJobLocations
          : placeEntries;
      const filtered = fallbackLocations.filter(ensureIsoCountry);
      if (filtered.length > 0) {
        schema.jobLocation = filtered;
        hasIso = true;
      }
    }
    if (!hasIso) {
      const hasLocationSignal =
        (Array.isArray(job.location) && job.location.some((loc) => String(loc).trim().length > 0)) ||
        placeEntries.length > 0 ||
        locationsArray.length > 0;
      if (hasLocationSignal && !remoteIndicated) {
        schema.jobLocation = [buildFinlandCountryOnlyJobLocationPlace()];
      } else {
        delete schema.jobLocation;
      }
    }
  }

  // List of all possible fields we try to fill
  const allFields = [
    'title',
    'description',
    'datePosted',
    'validThrough',
    'employmentType',
    'hiringOrganization',
    'jobLocation',
    'applicantLocationRequirements',
    'jobLocationType',
    'baseSalary',
    'directApply',
    'identifier',
    'educationRequirements',
    'experienceRequirements',
    'experienceInPlaceOfEducation',
    'incentiveCompensation',
    'industry',
    'jobBenefits',
    'jobImmediateStart',
    'occupationalCategory',
    'physicalRequirement',
    'qualifications',
    'responsibilities',
    'skills',
    'specialCommitments',
    'totalJobOpenings',
    'workHours',
    'image',
    'mainEntityOfPage',
    'url',
    'inLanguage',
  ];

  // Check for missing fields (required and recommended)
  const missingFields = allFields.filter((field) => {
    const val = schema[field];
    if (val === undefined || val === null) return true;
    if (typeof val === 'string' && val.trim() === '') return true;
    if (Array.isArray(val) && val.length === 0) return true;
    // For objects, check if all values are undefined/null/empty string
    if (typeof val === 'object' && val !== null) {
      return Object.values(val).every((v) => v === undefined || v === null || (typeof v === 'string' && v.trim() === ''));
    }
    return false;
  });

  const hasNonEmptyString = (value: unknown): value is string =>
    typeof value === 'string' && value.trim().length > 0;

  const hiringOrg = (schema.hiringOrganization ?? {}) as Record<string, unknown>;
  const identifier = (schema.identifier ?? {}) as Record<string, unknown>;
  const hasTelecommuteLocation = schema.jobLocationType === 'TELECOMMUTE';
  const hasOnsiteLocation = Array.isArray(schema.jobLocation) && schema.jobLocation.length > 0;
  const hasRemoteLocationRequirement = schema.applicantLocationRequirements !== undefined;

  const missingEssentialFields: string[] = [];
  if (!hasNonEmptyString(schema.title)) missingEssentialFields.push('title');
  if (!hasNonEmptyString(schema.description)) missingEssentialFields.push('description');
  if (!hasNonEmptyString(schema.datePosted)) missingEssentialFields.push('datePosted');
  if (!hasNonEmptyString(schema.validThrough)) missingEssentialFields.push('validThrough');
  if (!hasNonEmptyString(schema.employmentType)) missingEssentialFields.push('employmentType');
  if (!hasNonEmptyString(hiringOrg.name)) missingEssentialFields.push('hiringOrganization.name');
  if (!hasNonEmptyString(identifier.value)) missingEssentialFields.push('identifier.value');
  if (!hasNonEmptyString(schema.url)) missingEssentialFields.push('url');
  if (!hasNonEmptyString(schema.mainEntityOfPage)) missingEssentialFields.push('mainEntityOfPage');
  if (!hasNonEmptyString(schema.inLanguage)) missingEssentialFields.push('inLanguage');
  if (hasTelecommuteLocation) {
    if (!hasRemoteLocationRequirement) {
      missingEssentialFields.push('applicantLocationRequirements');
    }
  } else if (!hasOnsiteLocation) {
    missingEssentialFields.push('jobLocation');
  }

  const isContentComplete = missingEssentialFields.length === 0;

  return { schema, isContentComplete, missingFields };
}
